/*
 * Drives the WebAuthn ceremonies for the <webauthn-create> / <webauthn-get>
 * containers emitted by devise-webauthn's form helpers (passkey_creation_form_for,
 * login_with_passkey_form_for, login_with_security_key_form_for).
 *
 * devise-webauthn ships this behavior as custom elements, but our Babel config
 * (forceAllTransforms) downlevels ES6 classes, which customElements.define can't
 * accept. The markup and server contract are unchanged — only the JS is ours:
 * fetch the options from data-options-url, run the browser ceremony, drop the
 * credential JSON into the hidden input, then submit the form for real.
 * Server contract pinned by the Gemfile ("devise-webauthn", "~> 0.4.0"); the
 * gem's next release switches the options endpoints from GET to POST, so
 * revisit this file when bumping it.
 *
 * Page hooks:
 *   [data-webauthn-ui]                  hidden until support is confirmed
 *   [data-webauthn-unsupported-message] revealed when support is missing
 */

import { displayFlashMessage } from "./methods"

function isWebAuthnSupported() {
  return !!(
    navigator.credentials &&
    navigator.credentials.create &&
    navigator.credentials.get &&
    window.PublicKeyCredential &&
    PublicKeyCredential.parseCreationOptionsFromJSON &&
    PublicKeyCredential.parseRequestOptionsFromJSON
  )
}

$(document).on("turbolinks:load", function () {
  if (isWebAuthnSupported()) {
    $("[data-webauthn-ui]").removeAttr("hidden")
  } else {
    $("[data-webauthn-unsupported-message]").removeAttr("hidden")
  }
})

// Delegated once at the document level, so Turbolinks page swaps and cache
// restores can't double-bind it.
$(document).on("submit", "form", function (event) {
  var form = this
  var container = form.querySelector("webauthn-create, webauthn-get")
  if (!container) return

  var responseInput = container.querySelector(
    '[data-webauthn-target="response"]'
  )
  if (responseInput.value) return // ceremony finished — let the submit through

  event.preventDefault()
  var action = container.tagName === "WEBAUTHN-CREATE" ? "create" : "get"

  runCeremony(container, action)
    .then(function (credentialJson) {
      responseInput.value = credentialJson
      form.submit() // native submit skips this handler
    })
    .catch(function (error) {
      handleCeremonyError(error, action)
    })
})

async function runCeremony(container, action) {
  var response = await fetch(container.getAttribute("data-options-url"))
  if (!response.ok)
    throw new Error(
      "The passkey service is unavailable right now. Please try again."
    )
  var options = await response.json()

  if (action === "create") {
    var createOptions =
      PublicKeyCredential.parseCreationOptionsFromJSON(options)
    var created = await navigator.credentials.create({
      publicKey: createOptions,
    })
    return stringifyCreatedCredential(created)
  }

  var getOptions = PublicKeyCredential.parseRequestOptionsFromJSON(options)
  var credential = await navigator.credentials.get({ publicKey: getOptions })
  return stringifyRetrievedCredential(credential)
}

function handleCeremonyError(error, action) {
  if (error && error.name === "NotAllowedError") {
    // User closed the prompt or it timed out — not a failure worth alarming over
    displayFlashMessage("info", "Passkey prompt was cancelled.")
  } else if (action === "create") {
    displayFlashMessage(
      "danger",
      "Your passkey couldn't be created. Please try again."
    )
  } else {
    displayFlashMessage(
      "danger",
      "Passkey sign-in didn't complete. Please try again or use your password."
    )
  }

  if (window.console) console.error("WebAuthn " + action + " failed:", error)
}

/*
 * Serialization fallbacks ported from devise-webauthn's bundled JS: some
 * authenticators (notably 1Password) return credentials that JSON.stringify
 * chokes on, so rebuild the payload by hand when that happens.
 */
async function stringifyCreatedCredential(credential) {
  try {
    return JSON.stringify(credential)
  } catch (e) {
    if (window.console)
      console.warn(
        "Authenticator returned a malformed credential, attempting to fix it:",
        e
      )
  }

  var response = credential.response
  var publicKey = response.getPublicKey ? await response.getPublicKey() : null

  return JSON.stringify({
    type: credential.type,
    id: credential.id,
    rawId: credential.id,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: await credential.getClientExtensionResults(),
    response: {
      attestationObject: toBase64Url(response.attestationObject),
      authenticatorData: toBase64Url(response.authenticatorData),
      clientDataJSON: toBase64Url(response.clientDataJSON),
      publicKey: toBase64Url(publicKey),
      publicKeyAlgorithm: response.getPublicKeyAlgorithm(),
      transports: response.getTransports(),
    },
  })
}

async function stringifyRetrievedCredential(credential) {
  try {
    return JSON.stringify(credential)
  } catch (e) {
    if (window.console)
      console.warn(
        "Authenticator returned a malformed credential, attempting to fix it:",
        e
      )
  }

  var response = credential.response

  return JSON.stringify({
    type: credential.type,
    id: credential.id,
    rawId: credential.id,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: await credential.getClientExtensionResults(),
    response: {
      authenticatorData: toBase64Url(response.authenticatorData),
      clientDataJSON: toBase64Url(response.clientDataJSON),
      signature: toBase64Url(response.signature),
      userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
    },
  })
}

function toBase64Url(buffer) {
  if (!buffer) return null

  var binary = String.fromCharCode.apply(null, new Uint8Array(buffer))
  var base64 = btoa(binary)

  return base64.replace(/\+/g, "-").replace(/\//g, "_")
}
